import { PlaybackTypes } from './types';
import { Note, Flags } from '@fullstory/client-api';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { ActionType, createAction } from '@fullstory/redux-util/utils';
import type { SessionModel } from '@fullstory/playback-model';
import { EraserPublic } from '@fullstory/proto/fs/services/eraser';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  CreateShareLinkForNoteRequest,
  GetNotePreviewRequest,
  GetNotePreviewResponse,
  SessionNotesPublic,
} from '@fullstory/proto/fs/services/webber/internal/highlight';
import { identity } from 'lodash';

export const PlaybackActions = {
  clearSession: () => createAction({ type: PlaybackTypes.ClearSession }),
  loadSession: (session: SessionModel, sexpr: string, flags: Flags | null) =>
    createAction({
      type: PlaybackTypes.LoadSession,
      session,
      sexpr,
      flags, // Note, the flags are not stored in the redux state. They are needed to do some initial filtering on the session events.
    }),
  updateWithLiveData: (flags: Flags | null) =>
    createAction({
      type: PlaybackTypes.UpdateWithLiveData,
      flags,
    }),
  timeChange: (time: number) =>
    createAction({
      type: PlaybackTypes.TimeChange,
      time,
    }),
  clearEventTypesFilter: () =>
    createAction({
      type: PlaybackTypes.ClearEventTypesFilter,
    }),
  toggleEventTypeFilter: (eventType: string) =>
    createAction({
      type: PlaybackTypes.ToggleEventTypesFilter,
      eventType,
    }),
  setTextFilter: (filter: string) =>
    createAction({
      type: PlaybackTypes.SetTextFilter,
      filter,
    }),
  clearAllFilters: () =>
    createAction({
      type: PlaybackTypes.ClearAllFilters,
    }),
  addNote: (note: Note) =>
    createAction({
      type: PlaybackTypes.AddNote,
      note,
    }),
  addSentNote: (note: Note, integrationId: string, url: string) =>
    createAction({
      type: PlaybackTypes.AddSentNote,
      note,
      integrationId,
      url,
    }),
};

export const PlaybackRequestActions = {
  createShareLink: (req: CreateShareLinkForNoteRequest) =>
    createGRPCAction<typeof SessionNotesPublic.CreateShareLinkForNote>()(
      {
        type: PlaybackTypes.CreateShareLinkRequest,
        grpcFunction: SessionNotesPublic.CreateShareLinkForNote,
        readWrite: 'write',
        body: req,
        // An empty NoteId in the request will cause the server to generate a new NoteId, so always force this request
        // because two requests with empty Note IDs is valid, and will result in two different new ShareLinks and NoteIDs
        // being generated.
        force: true,
      },
      PlaybackTypes.CreateShareLinkSuccess,
      PlaybackTypes.CreateShareLinkFailure,
    ),
  deleteNote: (orgId: string, note: Note) =>
    createGRPCAction<typeof SessionNotesPublic.DeleteNote, Note>({
      modify: (_): Note => note,
    })(
      {
        type: PlaybackTypes.DeleteNoteRequest,
        grpcFunction: SessionNotesPublic.DeleteNote,
        readWrite: 'write',
        body: { orgId, userId: note.UserId, sessionId: note.SessionId, noteId: note.Id },
        force: true,
      },
      PlaybackTypes.DeleteNoteSuccess,
      PlaybackTypes.DeleteNoteFailure,
    ),
  deleteSession: (orgId: string, userId: int64, sessionId: int64) =>
    createGRPCAction<typeof EraserPublic.DeleteSession>()(
      {
        type: PlaybackTypes.DeleteSessionRequest,
        grpcFunction: EraserPublic.DeleteSession,
        body: { orgId, userId, sessionId, skipEmail: true },
        force: true,
        readWrite: 'write',
      },
      PlaybackTypes.DeleteSessionSuccess,
      PlaybackTypes.DeleteSessionFailure,
    ),
  getNotePreview: (req: GetNotePreviewRequest) =>
    createGRPCAction<typeof SessionNotesPublic.GetNotePreview, GetNotePreviewResponse>({
      modify: identity,
    })(
      {
        type: PlaybackTypes.GetNotePreviewRequest,
        body: req,
        grpcFunction: SessionNotesPublic.GetNotePreview,
        readWrite: 'read',
      },
      PlaybackTypes.GetNotePreviewSuccess,
      PlaybackTypes.GetNotePreviewFailure,
    ),
};

export type PlaybackActions =
  | ActionType<typeof PlaybackActions>
  | RequestActionType<typeof PlaybackRequestActions>;
